import {
  Box,
  FormControl,
  TextField,
  styled,
  MenuItem,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { updatePosition } from "../../utils/updatePosition";

const EllipsisMenuItem = styled(MenuItem)({
  fontSize: "14px",
  whiteSpace: "normal",
  wordBreak: "break-word",
});
const CustomTextField = styled(TextField)(({ error }) => ({
  width: "100%",
  "& .MuiInputBase-root": { height: "30px", p: 0, fontSize: "12px" },
  ...(error && { "& .MuiOutlinedInput-root": { borderColor: "#D34040" } }),
}));

const TableSelectField = ({
  defaultValue,
  newOption,
  title,
  value,
  handleChange,
  name,
  options,
  errors,
  height,
  disable,
}) => {
  const [customOptions, setCustomOptions] = useState([]);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newOptionValue, setNewOptionValue] = useState("");
  const [customError, setCustomError] = useState("");
  const menuProps = {
    PaperProps: { style: { maxHeight: "55%", overflowY: "auto" } },
  };

  // Re-add a saved custom value to the options when editing an existing row.
  useEffect(() => {
    if (
      value &&
      value !== "Tap to select" &&
      !(options || []).includes(value)
    ) {
      setCustomOptions((previous) =>
        previous.includes(value) ? previous : [...previous, value],
      );
      setNewOptionValue(value);
      setIsAddingNew(false);
    }
  }, [value, options]);

  const allOptions = updatePosition(
    [
      ...(options || []),
      ...customOptions,
      ...(newOption ? [newOption] : []),
    ].filter((option, index, list) => list.indexOf(option) === index),
  );

  const handleOptionChange = (event) => {
    const selectedValue = event.target.value;
    setIsAddingNew(selectedValue === "Others");
    if (selectedValue === "Others") {
      setNewOptionValue("");
      setCustomError("");
      handleChange({ target: { name, value: "" } });
    } else {
      handleChange(event);
    }
  };

  const handleNewOption = (event) => {
    setNewOptionValue(event.target.value);
    if (event.target.value.trim()) setCustomError("");
  };

  const commitNewOption = () => {
    const customValue = newOptionValue.trim();
    if (!customValue) {
      setCustomError("Please enter a value");
      return;
    }

    setCustomOptions((previous) =>
      previous.includes(customValue) ? previous : [...previous, customValue],
    );
    setIsAddingNew(false);
    handleChange({ target: { name, value: customValue } });
  };

  const handleNewOptionKeyDown = (event) => {
    if (event.key !== "Enter") return;
    event.preventDefault();
    commitNewOption();
  };

  return (
    <Box>
      {title && (
        <Typography variant="subtitle1" sx={{ fontWeight: "bold", mt: 1 }}>
          {title}
        </Typography>
      )}
      <FormControl fullWidth margin="normal" sx={{ mt: 0, p: 0 }}>
        {isAddingNew ? (
          <CustomTextField
            value={newOptionValue}
            name={name}
            onChange={handleNewOption}
            onBlur={commitNewOption}
            onKeyDown={handleNewOptionKeyDown}
            placeholder="New Option"
            autoFocus
            error={!!customError}
            sx={{ fontSize: "14px" }}
          />
        ) : (
          <CustomTextField
            select
            sx={{ fontSize: "14px" }}
            height={height}
            name={name}
            value={value || "Tap to select"}
            onChange={handleOptionChange}
            error={!!errors}
            SelectProps={{ MenuProps: menuProps }}
          >
            <MenuItem value="Tap to select" sx={{ fontSize: "14px" }}>
              {defaultValue || "Tap to select"}
            </MenuItem>
            {allOptions.map((option, index) =>
              option === "Others" ? (
                <EllipsisMenuItem key={index} value="Others">
                  Other (specify)
                </EllipsisMenuItem>
              ) : (
                <EllipsisMenuItem key={option?.id || index} disabled={disable === option} value={option?.id || option}>
                  {option?.name || option?.month || option}
                </EllipsisMenuItem>
              ),
            )}
          </CustomTextField>
        )}
        {customError && (
          <Typography sx={{ fontSize: "12px" }} color="#D34040">
            {customError}
          </Typography>
        )}
        {errors && (
          <Typography sx={{ fontSize: "12px" }} color="#D34040">
            {errors}
          </Typography>
        )}
      </FormControl>
    </Box>
  );
};

export default TableSelectField;
